import {
  useMemo,
  useState,
  ChangeEvent,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import { CellProps } from 'react-table';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { getReviewerList } from 'src/api/projects';
import { Vertical } from 'src/components/Alignments';
import UpdateAnnotatorDialog from 'src/components/Modal/UpdateAnnotatorDialog';
import { TableWrapper } from 'src/components/SharedTableStyles';
import Table from 'src/components/Table';
import useModality from 'src/hooks/useModality';
import { Modality } from 'src/types/api/data/image';
import { AnnotatorSchema, ReviewerSchema } from 'src/types/api/data/project';
import { Reviewer, Annotator } from 'src/types/client/user';
import { DEFAULT_PAGE_SIZE } from 'src/utils/constants';

// Interface
interface ReviewersProps {
  projectId: string;
  modality: Modality;
  reviewers?: ReviewerSchema[];
  annotators?: AnnotatorSchema[];
}

export default function Reviewers({
  projectId,
  reviewers: reviewersSchema,
  annotators: annotatorsSchema,
}: ReviewersProps): JSX.Element {
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const targetReviewer = useRef<Reviewer>();
  const modalityLabel = useModality();
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [annotators, setAnnotators] = useState<Annotator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const refreshReviewers = useCallback(() => {
    setRefreshCounter(prev => prev + 1);
  }, []);

  // Load reviewer and annotator lists
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setIsError(false);

    const loadData = async () => {
      const { getAnnotatorList } = await import('src/api/projects');

      // Load full annotator data with user info
      const annotatorsData = await getAnnotatorList(
        projectId,
        modalityLabel,
        annotatorsSchema
      );

      const reviewerList = await getReviewerList(
        projectId,
        modalityLabel,
        reviewersSchema
      );

      if (isMounted) {
        setAnnotators(annotatorsData);
        setReviewers(reviewerList);
        setIsLoading(false);
        setIsError(false);
      }
    };

    loadData().catch(() => {
      if (isMounted) {
        setReviewers([]);
        setAnnotators([]);
        setIsLoading(false);
        setIsError(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    projectId,
    modalityLabel,
    reviewersSchema,
    annotatorsSchema,
    refreshCounter,
  ]);

  // Create a lookup map for annotators to get associate names
  const annotatorMap = useMemo(() => {
    const map = new Map<string, Annotator>();
    annotators.forEach(annotator => {
      map.set(annotator.id, annotator);
    });
    return map;
  }, [annotators]);

  // Pagination
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(reviewers.length / DEFAULT_PAGE_SIZE);

  const handleClickAssign = (reviewer: Reviewer) => () => {
    targetReviewer.current = reviewer;
    setIsUpdateDialogOpen(true);
  };

  const handleChangePage = (event: ChangeEvent<unknown>, page: number) =>
    setPage(page);

  const handleAssignAnnotator = () => {
    refreshReviewers();
    targetReviewer.current = undefined;
    setIsUpdateDialogOpen(false);
  };

  // Table elements
  const reviewerColumn = useMemo(() => {
    return [
      {
        Header: 'Reviewer Username',
        accessor: 'username' as const,
      },
      {
        Header: 'Assigned Annotators',
        accessor: 'associates' as const,
        Cell: ({ cell: { value } }: CellProps<Reviewer, AnnotatorSchema[]>) => {
          return (
            <div>
              {value?.map(assoc => {
                // Look up the full annotator info from the annotators list
                const annotator = annotatorMap.get(assoc.id);
                const displayName =
                  annotator?.username || annotator?.name || 'No username';
                return <div key={assoc.id}>{displayName}</div>;
              }) || ''}
            </div>
          );
        },
      },
      {
        Header: 'Assign Annotators',
        id: 'targetUser',
        style: { width: '20%' },
        Cell: function renderAssignment(cell: CellProps<Reviewer, Reviewer>) {
          return (
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={handleClickAssign(cell.row.original)}
            >
              Assign
            </Button>
          );
        },
      },
    ];
  }, [annotatorMap]);

  const paginatedReviewers: Reviewer[] = useMemo(
    () =>
      reviewers.slice((page - 1) * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE),
    [reviewers, page]
  );

  const handleCloseUpdateAnnotatorDialog = () => {
    targetReviewer.current = undefined;
    setIsUpdateDialogOpen(false);
  };

  return (
    <Vertical>
      {targetReviewer.current && (
        <UpdateAnnotatorDialog
          open={isUpdateDialogOpen}
          projectId={projectId}
          onClose={handleCloseUpdateAnnotatorDialog}
          onUpdate={handleAssignAnnotator}
          reviewer={targetReviewer.current}
        />
      )}
      <Typography variant="body1" color="textSecondary">
        Manage reviewers and their assignments for this project.
      </Typography>
      <TableWrapper>
        <Table<Reviewer>
          columns={reviewerColumn}
          data={paginatedReviewers}
          isLoading={isLoading}
          isError={isError}
          onRefresh={refreshReviewers}
          paginationProps={{
            page,
            count: totalPages,
            onChange: handleChangePage,
          }}
          style={{ marginTop: 0 }}
        />
      </TableWrapper>
    </Vertical>
  );
}
